import React, { useEffect, useMemo, useState } from "react";

import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
  Table,
  Checkbox,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
} from "@mui/material";

const nfmt = (v) => {
  if (v === null || v === undefined) return "-";
  const num = Number(v);
  if (Number.isNaN(num)) return String(v);
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(num);
};

const isoToLocal = (s) => {
  if (!s) return "-";
  try {
    return new Date(s).toLocaleString();
  } catch {
    return s;
  }
};

function SimpleTable({ columns, rows, sortBy, sortDir, onSortChange }) {
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((c) => (
              <TableCell
                key={c.key}
                sx={{
                  fontWeight: 700,
                  cursor: onSortChange && c.key !== "__sel" ? "pointer" : "default",
                  userSelect: "none",
                  whiteSpace: "nowrap",
                }}
                onClick={() => {
                  if (!onSortChange) return;
                  if (c.key === "__sel") return;
                  onSortChange(c.key);
                }}
              >
                {c.label}
                {onSortChange && sortBy === c.key ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, idx) => (
            <TableRow key={r.id ?? idx}>
              {columns.map((c) => (
                <TableCell key={c.key}>{c.render ? c.render(r) : r[c.key]}</TableCell>
              ))}
            </TableRow>
          ))}

          {(!rows || rows.length === 0) && (
            <TableRow>
              <TableCell colSpan={columns.length} sx={{ opacity: 0.7 }}>
                No items.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function ActionsDashboard() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [limit, setLimit] = useState(50);
  const [staleDays, setStaleDays] = useState(30);
  const [overpricedMult, setOverpricedMult] = useState(1.05);
  const [minMargin, setMinMargin] = useState(12);

  const [data, setData] = useState({
    overview: {},
    stale_overpriced_listings: [],
    listings_missing_predicted: [],
    unlinked_best_offers: [],
    listings_missing_local_images: [],
    low_margin_listings: [],
  });

  const [selectedListingIds, setSelectedListingIds] = useState([]);
  const [adjustPercent, setAdjustPercent] = useState(-3);
  const [applying, setApplying] = useState(false);
  const [applyMsg, setApplyMsg] = useState("");

  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");

  // Client-side table controls (filter/sort) for Stale & Overpriced
  const [staleSearch, setStaleSearch] = useState("");
  const [stalePOSearch, setStalePOSearch] = useState("");
  const [staleSortBy, setStaleSortBy] = useState("days_since_update");
  const [staleSortDir, setStaleSortDir] = useState("desc");

  const handleStaleSortChange = (colKey) => {
    if (!colKey) return;
    if (staleSortBy !== colKey) {
      setStaleSortBy(colKey);
      setStaleSortDir("asc");
      return;
    }
    setStaleSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const qs = new URLSearchParams({
        limit: String(limit || 50),
        stale_days: String(staleDays || 30),
        overpriced_mult: String(overpricedMult || 1.05),
        min_margin: String(minMargin || 12),
      });

      const res = await fetch(`/api/actions/queue?${qs.toString()}`);
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const json = await res.json();
      setData(json || {});
      setSelectedListingIds([]);
    } catch (e) {
      setError(e?.message || "Failed to load action queue");
    } finally {
      setLoading(false);
    }
  };


  const applyBulkPercent = async () => {
    if (!selectedListingIds || selectedListingIds.length === 0) {
      setApplyMsg("No listings selected.");
      return;
    }
    setApplying(true);
    setApplyMsg("");
    try {
      const res = await fetch("/api/listings/bulk/price_adjust_and_sync", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: selectedListingIds, percent: Number(adjustPercent) }),
      });
      const json = await res.json();
      if (!res.ok) {
        throw new Error(json?.error || `HTTP ${res.status}`);
      }
      const failed = Array.isArray(json?.sync_failed) ? json.sync_failed.length : 0;
      setApplyMsg(`Updated ${json?.updated ?? 0} listing(s). Synced ${json?.synced ?? 0} to eBay.${failed ? ` ${failed} sync failed.` : ""}`);
      await load();
    } catch (e) {
      setApplyMsg(e?.message || "Bulk price update failed");
    } finally {
      setApplying(false);
    }
  };


  const syncSelectedLight = async () => {
    if (!selectedListingIds || selectedListingIds.length === 0) {
      setSyncMsg("No listings selected.");
      return;
    }
    setSyncing(true);
    setSyncMsg("");
    try {
      const res = await fetch("/api/listings/sync/light", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: selectedListingIds }),
      });
      const json = await res.json();
      if (!res.ok) {
        throw new Error(json?.error || `HTTP ${res.status}`);
      }
      const ok = Array.isArray(json?.synced) ? json.synced.length : 0;
      const failed = Array.isArray(json?.failed) ? json.failed.length : 0;
      setSyncMsg(`Synced ${ok} listing(s) from eBay.${failed ? ` ${failed} failed.` : ""}`);
      await load();
    } catch (e) {
      setSyncMsg(e?.message || "Sync selected failed");
    } finally {
      setSyncing(false);
    }
  };

  const syncSessionLight = async () => {
    if (!selectedListingIds || selectedListingIds.length === 0) {
      setSyncMsg("No listings selected.");
      return;
    }
    const poIds = new Set(
      selectedListingIds
        .map((id) => listingIdToPO.get(Number(id)))
        .filter((v) => v !== null && v !== undefined)
    );

    if (poIds.size !== 1) {
      setSyncMsg("Sync Session requires selected rows from a single PO.");
      return;
    }

    const poId = Array.from(poIds)[0];
    setSyncing(true);
    setSyncMsg("");
    try {
      const res = await fetch("/api/listings/sync/light/purchase-order", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ purchase_order_id: poId }),
      });
      const json = await res.json();
      if (!res.ok) {
        throw new Error(json?.error || `HTTP ${res.status}`);
      }
      const ok = Array.isArray(json?.synced) ? json.synced.length : 0;
      const failed = Array.isArray(json?.failed) ? json.failed.length : 0;
      setSyncMsg(`Synced PO ${poId}: ${ok} listing(s) from eBay.${failed ? ` ${failed} failed.` : ""}`);
      await load();
    } catch (e) {
      setSyncMsg(e?.message || "Sync session failed");
    } finally {
      setSyncing(false);
    }
  };

  const syncAllActiveLight = async () => {
    setSyncing(true);
    setSyncMsg("");
    try {
      const res = await fetch("/api/listings/sync/light/all-active", { method: "POST" });
      const json = await res.json();
      if (!res.ok) {
        throw new Error(json?.error || `HTTP ${res.status}`);
      }
      const ok = Array.isArray(json?.synced) ? json.synced.length : 0;
      const failed = Array.isArray(json?.failed) ? json.failed.length : 0;
      setSyncMsg(`Synced ${ok} active listing(s) from eBay.${failed ? ` ${failed} failed.` : ""}`);
      await load();
    } catch (e) {
      setSyncMsg(e?.message || "Sync all active failed");
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const overview = useMemo(() => data?.overview || {}, [data]);

  const priorityCards = useMemo(() => ([
    {
      key: "unlinked_best_offer_count",
      title: "Offers need linking",
      value: overview.unlinked_best_offer_count ?? 0,
      helper: "Live buyer activity that is not attached to a local listing yet.",
    },
    {
      key: "stale_overpriced_count",
      title: "Stale & overpriced",
      value: overview.stale_overpriced_count ?? 0,
      helper: `Approx excess above predicted: $${nfmt(overview.stale_overpriced_excess_value ?? 0)}`,
    },
    {
      key: "missing_predicted_count",
      title: "Missing predicted price",
      value: overview.missing_predicted_count ?? 0,
      helper: "Listings that still need a pricing estimate.",
    },
    {
      key: "low_margin_count",
      title: "Low expected margin",
      value: overview.low_margin_count ?? 0,
      helper: `Below ${nfmt(minMargin)}% expected margin.`,
    },
    {
      key: "missing_local_images_count",
      title: "Missing local images",
      value: overview.missing_local_images_count ?? 0,
      helper: "Listed items that have no local photos saved.",
    },
  ]), [overview, minMargin]);

  const nextAction = useMemo(() => {
    if ((overview.unlinked_best_offer_count ?? 0) > 0) {
      return "Start with unlinked best offers. They are the most time-sensitive buyer signal in the app.";
    }
    if ((overview.stale_overpriced_count ?? 0) > 0) {
      return `Next, work through stale overpriced listings. You have about $${nfmt(overview.stale_overpriced_excess_value ?? 0)} sitting above predicted pricing.`;
    }
    if ((overview.missing_predicted_count ?? 0) > 0) {
      return "Then fill in missing predicted prices so pricing decisions are based on data instead of guesswork.";
    }
    if ((overview.low_margin_count ?? 0) > 0) {
      return `Review listings under ${nfmt(minMargin)}% expected margin to protect profit.`;
    }
    if ((overview.missing_local_images_count ?? 0) > 0) {
      return "Restore or attach missing local images so listed inventory stays easy to audit and relist.";
    }
    return "No urgent action queue detected right now. This looks like a clean operating state.";
  }, [overview, minMargin]);

  const listingIdToPO = useMemo(() => {
    const map = new Map();
    const addRows = (rows) => {
      if (!Array.isArray(rows)) return;
      rows.forEach((r) => {
        if (r && r.listing_id != null) {
          map.set(Number(r.listing_id), r.purchase_order_id ?? null);
        }
      });
    };
    addRows(data?.stale_overpriced_listings);
    addRows(data?.listings_missing_predicted);
    addRows(data?.listings_missing_local_images);
    addRows(data?.low_margin_listings);
    return map;
  }, [data]);


  const staleDisplayedRows = useMemo(() => {
    const rows = Array.isArray(data?.stale_overpriced_listings) ? data.stale_overpriced_listings : [];

    const poFilter = String(stalePOSearch || "").trim();
    const poNum = poFilter ? Number(poFilter) : null;

    const t = String(staleSearch || "").trim().toLowerCase();

    const filtered = rows.filter((r) => {
      if (!r) return false;

      if (poFilter) {
        if (Number.isNaN(poNum)) return false;
        if (Number(r.purchase_order_id) !== poNum) return false;
      }

      if (t) {
        const hay = `${r.card_name || ""} ${r.sku || ""} ${r.listing_id || ""} ${r.purchase_order_id || ""}`.toLowerCase();
        if (!hay.includes(t)) return false;
      }

      return true;
    });

    const dir = staleSortDir === "asc" ? 1 : -1;
    const sortKey = staleSortBy;

    const getVal = (row) => {
      const v = row?.[sortKey];
      return v === null || v === undefined ? "" : v;
    };

    const sorted = [...filtered].sort((a, b) => {
      const av = getVal(a);
      const bv = getVal(b);

      const an = Number(av);
      const bn = Number(bv);
      const aNum = av !== "" && !Number.isNaN(an);
      const bNum = bv !== "" && !Number.isNaN(bn);

      if (aNum && bNum) return (an - bn) * dir;

      const ad = Date.parse(av);
      const bd = Date.parse(bv);
      const aDate = !Number.isNaN(ad);
      const bDate = !Number.isNaN(bd);
      if (aDate && bDate) return (ad - bd) * dir;

      return String(av).localeCompare(String(bv)) * dir;
    });

    return sorted;
  }, [data, stalePOSearch, staleSearch, staleSortBy, staleSortDir]);

  const staleCols = useMemo(
    () => [
      {
        key: "__sel",
        label: "",
        render: (r) => (
          <Checkbox
            size="small"
            checked={selectedListingIds.includes(r.listing_id)}
            onChange={(e) => {
              const checked = e.target.checked;
              setSelectedListingIds((prev) => {
                const set = new Set(prev || []);
                if (checked) set.add(r.listing_id);
                else set.delete(r.listing_id);
                return Array.from(set);
              });
            }}
          />
        ),
      },
      { key: "listing_id", label: "Listing ID" },
      { key: "sku", label: "SKU" },
      { key: "purchase_order_id", label: "PO" },
      { key: "buy_price", label: "Buy", render: (r) => nfmt(r.buy_price) },
      { key: "card_name", label: "Card" },
      { key: "listing_price", label: "Listed", render: (r) => nfmt(r.listing_price) },
      { key: "predicted_sale_price", label: "Predicted", render: (r) => nfmt(r.predicted_sale_price) },
      { key: "days_since_update", label: "Days Since Update" },
      { key: "updated_at", label: "Updated", render: (r) => isoToLocal(r.updated_at) },
    ],
    [selectedListingIds]
  );

  const missingPredCols = useMemo(
    () => [
      { key: "listing_id", label: "Listing ID" },
      { key: "sku", label: "SKU" },
      { key: "purchase_order_id", label: "PO" },
      { key: "buy_price", label: "Buy", render: (r) => nfmt(r.buy_price) },
      { key: "card_name", label: "Card" },
      { key: "listing_price", label: "Listed", render: (r) => nfmt(r.listing_price) },
      { key: "updated_at", label: "Updated", render: (r) => isoToLocal(r.updated_at) },
    ],
    []
  );

  const missingLocalImageCols = useMemo(
    () => [
      { key: "listing_id", label: "Listing ID" },
      { key: "sku", label: "SKU" },
      { key: "purchase_order_id", label: "PO" },
      { key: "buy_price", label: "Buy", render: (r) => nfmt(r.buy_price) },
      { key: "card_name", label: "Card" },
      { key: "listing_price", label: "Listed", render: (r) => nfmt(r.listing_price) },
      { key: "photo_count", label: "Photos" },
      { key: "updated_at", label: "Updated", render: (r) => isoToLocal(r.updated_at) },
    ],
    []
  );

  const lowMarginCols = useMemo(
    () => [
      { key: "listing_id", label: "Listing ID" },
      { key: "sku", label: "SKU" },
      { key: "purchase_order_id", label: "PO" },
      { key: "buy_price", label: "Buy", render: (r) => nfmt(r.buy_price) },
      { key: "card_name", label: "Card" },
      { key: "listing_price", label: "Listed", render: (r) => nfmt(r.listing_price) },
      { key: "predicted_sale_price", label: "Predicted", render: (r) => nfmt(r.predicted_sale_price) },
      { key: "expected_profit", label: "Expected Profit", render: (r) => nfmt(r.expected_profit) },
      { key: "expected_margin", label: "Margin %", render: (r) => nfmt(r.expected_margin) },
      { key: "updated_at", label: "Updated", render: (r) => isoToLocal(r.updated_at) },
    ],
    []
  );

  const offersCols = useMemo(
    () => [
      { key: "id", label: "Row ID" },
      { key: "best_offer_id", label: "BestOfferID" },
      { key: "item_id", label: "Item ID" },
      { key: "offer_status", label: "Status" },
      { key: "offer_price", label: "Offer", render: (r) => nfmt(r.offer_price) },
      { key: "offered_at", label: "Offered", render: (r) => isoToLocal(r.offered_at) },
    ],
    []
  );

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "stretch", md: "center" }}>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>
          Actions
        </Typography>

        <Box sx={{ flex: 1 }} />

        <TextField
          size="small"
          label="Limit"
          type="number"
          value={limit}
          onChange={(e) => setLimit(Number(e.target.value))}
          sx={{ width: 120 }}
        />
        <TextField
          size="small"
          label="Stale Days"
          type="number"
          value={staleDays}
          onChange={(e) => setStaleDays(Number(e.target.value))}
          sx={{ width: 140 }}
        />
        <TextField
          size="small"
          label="Overpriced x"
          type="number"
          inputProps={{ step: 0.01 }}
          value={overpricedMult}
          onChange={(e) => setOverpricedMult(Number(e.target.value))}
          sx={{ width: 150 }}
        />
        <TextField
          size="small"
          label="Min Margin %"
          type="number"
          inputProps={{ step: 0.5 }}
          value={minMargin}
          onChange={(e) => setMinMargin(Number(e.target.value))}
          sx={{ width: 150 }}
        />

        <Button variant="contained" onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      {error && (
        <Typography sx={{ mt: 1, color: "error.main" }}>
          {error}
        </Typography>
      )}

      {loading && (
        <Stack direction="row" spacing={1} sx={{ mt: 2 }} alignItems="center">
          <CircularProgress size={18} />
          <Typography>Loading…</Typography>
        </Stack>
      )}

      <Stack spacing={2} sx={{ mt: 2 }}>
        <Card sx={{ borderRadius: 3 }} variant="outlined">
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
              Operations summary
            </Typography>
            <Typography sx={{ opacity: 0.75, mb: 2 }}>
              The Actions view now surfaces the most urgent workflow gaps first so you can work the app as an operations cockpit, not just a set of raw tables.
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(5, 1fr)" },
                gap: 1.5,
              }}
            >
              {priorityCards.map((card) => (
                <Paper key={card.key} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ opacity: 0.75 }}>
                    {card.title}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5 }}>
                    {nfmt(card.value)}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.75, mt: 0.5 }}>
                    {card.helper}
                  </Typography>
                </Paper>
              ))}
            </Box>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mt: 2 }}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, flex: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Recommended next action
                </Typography>
                <Typography sx={{ opacity: 0.82 }}>
                  {nextAction}
                </Typography>
              </Paper>

              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Portfolio health
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.82 }}>
                  Avg listed margin: {nfmt(overview.avg_listed_margin ?? 0)}%
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.82 }}>
                  Oldest stale listing: {nfmt(overview.oldest_stale_days ?? 0)} days
                </Typography>
              </Paper>
            </Stack>
          </CardContent>
        </Card>
        <Card sx={{ borderRadius: 3 }} variant="outlined">
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
              Stale & overpriced listings
            </Typography>
            <Typography sx={{ opacity: 0.75, mb: 1 }}>
              Listed listings not updated for ≥ {staleDays} days and priced above predicted × {overpricedMult}.
            </Typography>


            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }} sx={{ mb: 1 }}>
              <TextField
                size="small"
                label="Search (Card/SKU/Listing/PO)"
                value={staleSearch}
                onChange={(e) => setStaleSearch(e.target.value)}
                sx={{ minWidth: 260 }}
              />
              <TextField
                size="small"
                label="PO"
                type="number"
                value={stalePOSearch}
                onChange={(e) => setStalePOSearch(e.target.value)}
                sx={{ width: 140 }}
              />
              <TextField
                size="small"
                select
                label="Sort By"
                value={staleSortBy}
                onChange={(e) => setStaleSortBy(e.target.value)}
                sx={{ width: 180 }}
              >
                <MenuItem value="days_since_update">Days Since Update</MenuItem>
                <MenuItem value="listing_price">Listed Price</MenuItem>
                <MenuItem value="predicted_sale_price">Predicted Price</MenuItem>
                <MenuItem value="buy_price">Buy Price</MenuItem>
                <MenuItem value="updated_at">Updated</MenuItem>
                <MenuItem value="purchase_order_id">PO</MenuItem>
                <MenuItem value="sku">SKU</MenuItem>
                <MenuItem value="card_name">Card</MenuItem>
                <MenuItem value="listing_id">Listing ID</MenuItem>
              </TextField>
              <TextField
                size="small"
                select
                label="Dir"
                value={staleSortDir}
                onChange={(e) => setStaleSortDir(e.target.value)}
                sx={{ width: 110 }}
              >
                <MenuItem value="asc">Asc</MenuItem>
                <MenuItem value="desc">Desc</MenuItem>
              </TextField>

              <Box sx={{ flex: 1 }} />

              <Typography variant="body2" sx={{ opacity: 0.75 }}>
                Showing {(staleDisplayedRows || []).length} of {(data?.stale_overpriced_listings || []).length}
              </Typography>

              <Button
                variant="text"
                onClick={() => {
                  setStaleSearch("");
                  setStalePOSearch("");
                  setStaleSortBy("days_since_update");
                  setStaleSortDir("desc");
                }}
              >
                Clear
              </Button>
            </Stack>


            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }} sx={{ mb: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Checkbox
                  size="small"
                  checked={
                    (staleDisplayedRows || []).length > 0 &&
                    selectedListingIds.filter((id) => (staleDisplayedRows || []).some((r) => r.listing_id === id)).length === (staleDisplayedRows || []).length
                  }
                  indeterminate={
                    selectedListingIds.filter((id) => (staleDisplayedRows || []).some((r) => r.listing_id === id)).length > 0 &&
                    selectedListingIds.filter((id) => (staleDisplayedRows || []).some((r) => r.listing_id === id)).length < (staleDisplayedRows || []).length
                  }
                  onChange={(e) => {
                    const checked = e.target.checked;
                    if (checked) {
                      setSelectedListingIds((staleDisplayedRows || []).map((r) => r.listing_id));
                    } else {
                      setSelectedListingIds([]);
                    }
                  }}
                />
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Select all
                </Typography>
              </Stack>

              <TextField
                size="small"
                label="Adjust %"
                type="number"
                value={adjustPercent}
                onChange={(e) => setAdjustPercent(e.target.value)}
                sx={{ width: 140 }}
                inputProps={{ step: 0.5 }}
              />

              <Button
                variant="contained"
                disabled={applying || selectedListingIds.length === 0}
                onClick={applyBulkPercent}
              >
                {applying ? "Applying..." : "Apply to selected (sync eBay)"}
              </Button>

              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                <Button
                  variant="outlined"
                  disabled={syncing || selectedListingIds.length === 0}
                  onClick={syncSelectedLight}
                >
                  {syncing ? "Syncing..." : "Sync Selected (Light)"}
                </Button>

                <Button
                  variant="outlined"
                  disabled={syncing || selectedListingIds.length === 0}
                  onClick={syncSessionLight}
                >
                  {syncing ? "Syncing..." : "Sync Session (Light)"}
                </Button>

                <Button variant="outlined" disabled={syncing} onClick={syncAllActiveLight}>
                  {syncing ? "Syncing..." : "Sync All Active (Light)"}
                </Button>
              </Stack>

              {syncMsg && (
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  {syncMsg}
                </Typography>
              )}

              {applyMsg && (
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  {applyMsg}
                </Typography>
              )}
            </Stack>

            <SimpleTable
              columns={staleCols}
              rows={staleDisplayedRows || []}
              sortBy={staleSortBy}
              sortDir={staleSortDir}
              onSortChange={handleStaleSortChange}
            />
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 3 }} variant="outlined">
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
              Listed but missing predicted price
            </Typography>
            <Typography sx={{ opacity: 0.75, mb: 1 }}>
              Usually means the listing needs a scrape/estimate so pricing decisions can be made.
            </Typography>
            <SimpleTable columns={missingPredCols} rows={data.listings_missing_predicted || []} />
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 3 }} variant="outlined">
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
              Listed but missing local images
            </Typography>
            <Typography sx={{ opacity: 0.75, mb: 1 }}>
              These listings are live, but the local record has no stored photo set. That makes relisting, auditing, and content checks harder.
            </Typography>
            <SimpleTable columns={missingLocalImageCols} rows={data.listings_missing_local_images || []} />
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 3 }} variant="outlined">
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
              Low expected margin listings
            </Typography>
            <Typography sx={{ opacity: 0.75, mb: 1 }}>
              Active listings with expected margin below {nfmt(minMargin)}%. This is a good place to protect profit before volume masks weak items.
            </Typography>
            <SimpleTable columns={lowMarginCols} rows={data.low_margin_listings || []} />
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 3 }} variant="outlined">
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
              Unlinked best offers
            </Typography>
            <Typography sx={{ opacity: 0.75, mb: 1 }}>
              Active/Pending offers that aren’t linked to a local listing yet.
            </Typography>
            <SimpleTable columns={offersCols} rows={data.unlinked_best_offers || []} />
          </CardContent>
        </Card>

        <Divider />

        <Typography sx={{ opacity: 0.7 }}>
          Note: This module is intentionally “action-first” (no charts). It’s meant to surface the next 10–50 money-making tasks.
        </Typography>
      </Stack>
    </Box>
  );
}
